import {
  PaymentTransaction,
  TransactionFilters,
  TransactionOption,
  TransactionPaymentType,
} from '../types';

export const TRANSACTIONS_TABLE_HEAD = [
  { id: 'id', label: 'ID', width: 80 },
  { id: 'created_at', label: 'Created', minWidth: 160 },
  { id: 'source', label: 'Source', minWidth: 120 },
  { id: 'type', label: 'Type', minWidth: 120 },
  { id: 'booking_id', label: 'Booking', minWidth: 120 },
  { id: 'user_id', label: 'User', minWidth: 120 },
  { id: 'amount', label: 'Amount', minWidth: 120 },
  { id: 'status', label: 'Status', minWidth: 120 },
  { id: 'payment_intent', label: 'Payment Intent', minWidth: 220 },
  { id: 'wallet', label: 'Wallet', minWidth: 200 },
];

type LabelColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

type StatusPresentation = {
  label: string;
  color: LabelColor;
};

type TransactionStatusInput = PaymentTransaction['status'] | PaymentTransaction;

const STATUS_TEXT_PRESETS: Record<string, StatusPresentation> = {
  succeeded: { label: 'Succeeded', color: 'success' },
  success: { label: 'Success', color: 'success' },
  paid: { label: 'Paid', color: 'success' },
  pending: { label: 'Pending', color: 'warning' },
  processing: { label: 'Processing', color: 'info' },
  created: { label: 'Created', color: 'info' },
  requires_action: { label: 'Requires Action', color: 'warning' },
  requires_payment_method: { label: 'Needs Payment Method', color: 'warning' },
  failed: { label: 'Failed', color: 'error' },
  cancelled: { label: 'Cancelled', color: 'default' },
  canceled: { label: 'Cancelled', color: 'default' },
  refunded: { label: 'Refunded', color: 'info' },
  partially_refunded: { label: 'Partially Refunded', color: 'info' },
  reversed: { label: 'Reversed', color: 'warning' },
  onhold: { label: 'On Hold', color: 'warning' },
};

const NUMERIC_STATUS_MAP: Record<number, keyof typeof STATUS_TEXT_PRESETS> = {
  0: 'pending',
  1: 'succeeded',
  2: 'onhold',
  3: 'failed',
  4: 'processing',
  5: 'cancelled',
};

const REFUND_PAYMENT_TYPE_IDS = new Set([7, 8]);

const isTransactionRecord = (
  value: TransactionStatusInput
): value is PaymentTransaction =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getMetadataValue = (
  transaction: Pick<PaymentTransaction, 'metadata'>,
  key: string
) => {
  const { metadata } = transaction;
  if (!metadata || typeof metadata !== 'object') return null;

  const value = metadata[key];
  return typeof value === 'string' || typeof value === 'number' ? value : null;
};

const hasReferenceValue = (value: unknown) => {
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  return typeof value === 'string' && value.trim().length > 0;
};

export const isRefundLikeTransaction = (
  transaction?: Pick<
    PaymentTransaction,
    | 'display_type'
    | 'is_refund'
    | 'metadata'
    | 'payment_type'
    | 'refund_reason'
    | 'refunded_transaction_id'
    | 'stripe_refund_id'
    | 'type'
  > | null
) => {
  if (!transaction) return false;

  const paymentTypeName = transaction.payment_type?.name?.toLowerCase() ?? '';
  const paymentTypeDisplayName =
    transaction.payment_type?.display_name?.toLowerCase() ?? '';
  const displayType = transaction.display_type?.toLowerCase() ?? '';
  const stripeRefundId =
    transaction.stripe_refund_id?.trim() ||
    getMetadataValue(transaction, 'stripe_refund_id');
  const refundedTransactionId =
    transaction.refunded_transaction_id ??
    getMetadataValue(transaction, 'refunded_transaction_id');
  const refundReason =
    transaction.refund_reason?.trim() ||
    getMetadataValue(transaction, 'refund_reason');

  return (
    transaction.is_refund === true ||
    REFUND_PAYMENT_TYPE_IDS.has(Number(transaction.type)) ||
    paymentTypeName.includes('refund') ||
    paymentTypeDisplayName.includes('refund') ||
    displayType.includes('refund') ||
    hasReferenceValue(stripeRefundId) ||
    hasReferenceValue(refundedTransactionId) ||
    hasReferenceValue(refundReason)
  );
};

export const isVoidLikeTransaction = (
  transaction?: Pick<
    PaymentTransaction,
    | 'display_type'
    | 'is_void'
    | 'metadata'
    | 'payment_type'
    | 'source'
    | 'type'
    | 'void_reason'
    | 'voided_payment_transaction_id'
    | 'voided_transaction_id'
    | 'reversed_transaction_id'
  > | null
) => {
  if (!transaction) return false;

  const paymentTypeName = transaction.payment_type?.name?.toLowerCase() ?? '';
  const paymentTypeDisplayName =
    transaction.payment_type?.display_name?.toLowerCase() ?? '';
  const displayType = transaction.display_type?.toLowerCase() ?? '';
  const source = transaction.source?.toLowerCase() ?? '';
  const voidReason =
    transaction.void_reason?.trim() ||
    getMetadataValue(transaction, 'void_reason');
  const voidedTransactionId =
    transaction.voided_transaction_id ??
    getMetadataValue(transaction, 'voided_transaction_id');
  const voidedPaymentTransactionId =
    transaction.voided_payment_transaction_id ??
    getMetadataValue(transaction, 'voided_payment_transaction_id');
  const reversedTransactionId =
    transaction.reversed_transaction_id ??
    getMetadataValue(transaction, 'reversed_transaction_id');
  const hasVoidText = [
    paymentTypeName,
    paymentTypeDisplayName,
    displayType,
    source,
  ]
    .filter(Boolean)
    .some(
      (value) =>
        value.includes('void') ||
        value.includes('reversal') ||
        value.includes('reversed')
    );

  return (
    transaction.is_void === true ||
    hasVoidText ||
    hasReferenceValue(voidReason) ||
    hasReferenceValue(voidedTransactionId) ||
    hasReferenceValue(voidedPaymentTransactionId) ||
    hasReferenceValue(reversedTransactionId)
  );
};

const getStatusFromInput = (input: TransactionStatusInput) =>
  isTransactionRecord(input) ? input.status : input;

export const getTransactionStatusPresentation = (
  input: TransactionStatusInput
): StatusPresentation => {
  if (isTransactionRecord(input) && isVoidLikeTransaction(input)) {
    return STATUS_TEXT_PRESETS.reversed;
  }

  if (isTransactionRecord(input) && isRefundLikeTransaction(input)) {
    return STATUS_TEXT_PRESETS.refunded;
  }

  const status = getStatusFromInput(input);

  if (status === null || typeof status === 'undefined') {
    return { label: 'Unknown', color: 'default' };
  }

  if (typeof status === 'number') {
    const mappedKey = NUMERIC_STATUS_MAP[status];
    if (mappedKey) {
      return STATUS_TEXT_PRESETS[mappedKey];
    }
    return { label: String(status), color: 'default' };
  }

  const normalized = status.toString().trim().toLowerCase();
  if (/^-?\d+$/.test(normalized)) {
    const mappedKey = NUMERIC_STATUS_MAP[Number(normalized)];
    if (mappedKey) {
      return STATUS_TEXT_PRESETS[mappedKey];
    }
  }

  if (STATUS_TEXT_PRESETS[normalized]) {
    return STATUS_TEXT_PRESETS[normalized];
  }

  return { label: status.toString(), color: 'default' };
};

export const formatTransactionAmount = (
  amount?: number | null,
  currency?: string | null
) => {
  const normalizedAmount = typeof amount === 'number' ? amount : 0;
  const normalizedCurrency = (currency || 'GBP').toUpperCase();

  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: normalizedCurrency,
      currencyDisplay: 'symbol',
    }).format(normalizedAmount);
  } catch (error) {
    return `${normalizedCurrency} ${normalizedAmount.toFixed(2)}`;
  }
};

export const formatTransactionType = (
  type?: PaymentTransaction['type']
): string => {
  if (typeof type === 'number') {
    return String(type);
  }
  if (!type) {
    return 'N/A';
  }
  return type
    .toString()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

type ApplySortFilterArgs = {
  tableData: PaymentTransaction[];
  comparator: (a: PaymentTransaction, b: PaymentTransaction) => number;
  filters: TransactionFilters;
};

const SOURCE_LABEL_MAP: Record<string, string> = {
  '1': 'Web',
};

const containsText = (value: unknown, query: string) => {
  if (!value) return false;
  return value.toString().toLowerCase().includes(query);
};

const resolvePaymentTypeName = (paymentType?: TransactionPaymentType | null) =>
  paymentType?.name?.trim();

export const getTransactionTypeLabel = (transaction: PaymentTransaction) => {
  const paymentTypeName = resolvePaymentTypeName(transaction.payment_type);
  if (paymentTypeName) {
    return paymentTypeName;
  }

  return formatTransactionType(transaction.type);
};

export const formatTransactionSource = (
  source?: PaymentTransaction['source']
) => {
  if (!source) {
    return '—';
  }

  const normalized = source.toString();
  return SOURCE_LABEL_MAP[normalized] ?? normalized;
};

const normalizeTransactionStatusValue = (
  status: PaymentTransaction['status']
) => {
  if (status === null || typeof status === 'undefined') {
    return null;
  }

  if (typeof status === 'number') {
    return String(status);
  }

  return status.toString().trim().toLowerCase();
};

const getTransactionStatusFilterValue = (transaction: PaymentTransaction) => {
  if (isRefundLikeTransaction(transaction)) {
    return 'refunded';
  }

  return normalizeTransactionStatusValue(transaction.status);
};

export const buildTransactionStatusOptions = (
  transactions: PaymentTransaction[]
): TransactionOption[] => {
  const map = new Map<string, string>();

  transactions.forEach((transaction) => {
    const normalizedStatus = getTransactionStatusFilterValue(transaction);

    if (!normalizedStatus || map.has(normalizedStatus)) {
      return;
    }

    const presentation = getTransactionStatusPresentation(transaction);
    map.set(normalizedStatus, presentation.label);
  });

  return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
};

export const buildTransactionTypeOptions = (
  transactions: PaymentTransaction[]
): TransactionOption[] => {
  const map = new Map<string, string>();

  transactions.forEach((transaction) => {
    if (!transaction.type) {
      return;
    }

    const normalizedType = transaction.type.toString().toLowerCase();

    if (!map.has(normalizedType)) {
      map.set(normalizedType, getTransactionTypeLabel(transaction));
    }
  });

  return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
};

export const buildTransactionSourceOptions = (
  transactions: PaymentTransaction[]
): TransactionOption[] => {
  const map = new Map<string, string>();

  transactions.forEach((transaction) => {
    if (!transaction.source) {
      return;
    }

    const normalizedSource = transaction.source.toString().toLowerCase();

    if (!map.has(normalizedSource)) {
      map.set(normalizedSource, formatTransactionSource(transaction.source));
    }
  });

  return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
};

export function applySortFilterTransactions({
  tableData,
  comparator,
  filters,
}: ApplySortFilterArgs) {
  const stabilizedThis = tableData.map((el, index) => [el, index] as const);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  let sortedData = stabilizedThis.map((el) => el[0]);

  const query = filters.query.trim().toLowerCase();

  if (query) {
    sortedData = sortedData.filter((transaction) => {
      const { wallet } = transaction;
      const candidates = [
        transaction.id,
        transaction.booking_id,
        transaction.user_id,
        transaction.source,
        transaction.type,
        resolvePaymentTypeName(transaction.payment_type),
        transaction.status,
        transaction.payment_intent,
        transaction.idempotency_key,
        transaction.currency,
        wallet?.name,
        wallet?.brand,
        wallet?.last4 ? `••••${wallet.last4}` : '',
        transaction.booking?.uuid,
        transaction.users?.first_name,
        transaction.users?.last_name,
        transaction.users?.email,
      ];
      return candidates.some((value) => containsText(value, query));
    });
  }

  if (filters.status) {
    const normalizedStatus = filters.status.toLowerCase();
    sortedData = sortedData.filter((transaction) => {
      const presentation = getTransactionStatusPresentation(transaction);
      const transactionStatus = getTransactionStatusFilterValue(transaction);
      return (
        presentation.label.toLowerCase() === normalizedStatus ||
        transactionStatus === normalizedStatus
      );
    });
  }

  if (filters.type) {
    const normalizedType = filters.type.toLowerCase();
    sortedData = sortedData.filter(
      (transaction) =>
        transaction.type?.toString().toLowerCase() === normalizedType
    );
  }

  if (filters.source) {
    const normalizedSource = filters.source.toLowerCase();
    sortedData = sortedData.filter(
      (transaction) => transaction.source?.toLowerCase() === normalizedSource
    );
  }

  return sortedData;
}

export const isFiltersApplied = (filters: TransactionFilters) =>
  Boolean(filters.query || filters.status || filters.type || filters.source);
